import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// ============================================================
// STRIPE DASHBOARD — Admin Portal Component
// ============================================================
// Provides admin visibility into the Stripe Connect integration:
// - Connected accounts and their onboarding status
// - Platform balance (fees collected)
// - Recent payment transactions
// ============================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

// Types for the data returned by the backend
interface ConnectedAccount {
  profileId: string;
  name: string;
  email: string;
  serviceType: string;
  profileStatus: string;
  stripeAccountId: string;
  onboardingComplete: boolean;
  readyToReceivePayments: boolean;
  requirementsStatus: string;
  error?: string;
}

interface BalanceEntry {
  amount: number;
  currency: string;
}

interface PlatformBalance {
  available: BalanceEntry[];
  pending: BalanceEntry[];
}

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: string;
  providerProfileId: string | null;
  serviceName: string | null;
  providerName: string | null;
  applicationFeeAmount: number | null;
}

// Helper to get the admin auth token
function getToken(): string | null {
  return localStorage.getItem('admin_token');
}

// Helper to make authenticated requests to the backend
async function fetchWithAuth(endpoint: string) {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Service type labels and colors
const SERVICE_TYPE_LABELS: Record<string, string> = {
  sitter: 'Pet Sitter',
  groomer: 'Pet Groomer',
  vet: 'Veterinarian',
  trainer: 'Pet Trainer',
};

const SERVICE_TYPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
  sitter: 'primary',
  groomer: 'secondary',
  vet: 'success',
  trainer: 'warning',
};

// Payment status chip colors
function getPaymentStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'succeeded': return 'success';
    case 'processing': return 'warning';
    case 'requires_payment_method':
    case 'requires_action':
    case 'canceled': return 'error';
    default: return 'default';
  }
}

export default function StripeDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [balance, setBalance] = useState<PlatformBalance | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [heldPayouts, setHeldPayouts] = useState<any[]>([]);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [accountsData, balanceData, paymentsData, heldData] = await Promise.all([
        fetchWithAuth('/api/stripe/admin/connected-accounts').catch(() => ({ accounts: [] })),
        fetchWithAuth('/api/stripe/admin/balance').catch(() => null),
        fetchWithAuth('/api/stripe/admin/payments?limit=20').catch(() => ({ payments: [] })),
        fetchWithAuth('/api/bookings/held-payouts').catch(() => ({ bookings: [] })),
      ]);

      setAccounts(accountsData.accounts || []);
      setBalance(balanceData);
      setPayments(paymentsData.payments || []);
      setHeldPayouts(heldData.bookings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load Stripe data');
    } finally {
      setLoading(false);
    }
  }

  const handleReleasePayout = async (bookingId: string) => {
    setReleasingId(bookingId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/release-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        alert('Payout released successfully');
        loadData();
      } else {
        alert(`Failed: ${data.error || 'Unknown error'}${data.details ? ' - ' + data.details : ''}`);
      }
    } catch (err) {
      alert('Failed to release payout');
    } finally {
      setReleasingId(null);
    }
  };

  // Calculate summary stats
  const totalAccounts = accounts.length;
  const activeAccounts = accounts.filter(a => a.readyToReceivePayments).length;
  const pendingOnboarding = accounts.filter(a => !a.onboardingComplete).length;
  const availableBalance = balance?.available?.reduce((sum, b) => sum + b.amount, 0) || 0;
  const pendingBalance = balance?.pending?.reduce((sum, b) => sum + b.amount, 0) || 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Stripe Payments
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Connected Accounts */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PeopleIcon sx={{ fontSize: 40, color: '#276EF1' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">{totalAccounts}</Typography>
                <Typography variant="body2" color="text.secondary">Connected Accounts</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active / Ready */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#0E8345' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">{activeAccounts}</Typography>
                <Typography variant="body2" color="text.secondary">Active (Can Receive Payments)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Onboarding */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon sx={{ fontSize: 40, color: '#F6B100' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">{pendingOnboarding}</Typography>
                <Typography variant="body2" color="text.secondary">Pending Onboarding</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Balance */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AccountBalanceIcon sx={{ fontSize: 40, color: '#276EF1' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  ${availableBalance.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available ({pendingBalance > 0 ? `$${pendingBalance.toFixed(2)} pending` : 'no pending'})
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs: Connected Accounts / Recent Payments */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Connected Accounts" />
        <Tab icon={<PaymentIcon />} iconPosition="start" label="Recent Payments" />
        <Tab icon={<HourglassEmptyIcon />} iconPosition="start" label={`Held Payouts${heldPayouts.length > 0 ? ` (${heldPayouts.length})` : ''}`} />
      </Tabs>

      {/* Tab 0: Connected Accounts */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Stripe Account</strong></TableCell>
                <TableCell><strong>Onboarding</strong></TableCell>
                <TableCell><strong>Payments</strong></TableCell>
                <TableCell><strong>Requirements</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No connected accounts yet. Accounts are created when providers are approved.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.profileId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{account.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{account.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={SERVICE_TYPE_LABELS[account.serviceType] || account.serviceType}
                        color={SERVICE_TYPE_COLORS[account.serviceType] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {account.stripeAccountId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {account.onboardingComplete ? (
                        <Chip icon={<CheckCircleIcon />} label="Complete" color="success" size="small" />
                      ) : (
                        <Chip icon={<WarningIcon />} label="Incomplete" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {account.readyToReceivePayments ? (
                        <Chip icon={<CheckCircleIcon />} label="Active" color="success" size="small" />
                      ) : (
                        <Chip icon={<ErrorIcon />} label="Not Ready" color="error" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.requirementsStatus}
                        size="small"
                        variant="outlined"
                        color={
                          account.requirementsStatus === 'none' ? 'success' :
                          account.requirementsStatus === 'currently_due' ? 'warning' :
                          account.requirementsStatus === 'past_due' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 1: Recent Payments */}
      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Platform Fee</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Payment ID</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No payments yet. Payments will appear here when pet owners book services.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      {new Date(payment.created).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="500">
                        ${payment.amount.toFixed(2)} {payment.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.applicationFeeAmount
                        ? `$${payment.applicationFeeAmount.toFixed(2)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        color={getPaymentStatusColor(payment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{payment.serviceName || '—'}</TableCell>
                    <TableCell>{payment.providerName || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {payment.id.substring(0, 20)}...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 3: Held Payouts */}
      {tabValue === 3 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Payout Amount</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {heldPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No held payouts</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                heldPayouts.map((b: any) => (
                  <TableRow key={b._id}>
                    <TableCell>{b.bookingNumber || b._id.substring(0, 8)}</TableCell>
                    <TableCell>{b.providerName || '—'}</TableCell>
                    <TableCell>{b.service}</TableCell>
                    <TableCell>{new Date(b.endDate).toLocaleDateString()}</TableCell>
                    <TableCell>{b.payoutAmount?.toFixed(2)} {b.currency}</TableCell>
                    <TableCell>{b.payoutReleaseDate ? new Date(b.payoutReleaseDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={b.payoutStatus === 'released' ? 'Released' : b.payoutStatus === 'failed' ? 'Failed' : 'Held'}
                        color={b.payoutStatus === 'released' ? 'success' : b.payoutStatus === 'failed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {b.payoutStatus === 'held' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disabled={releasingId === b._id}
                          onClick={() => handleReleasePayout(b._id)}
                        >
                          {releasingId === b._id ? 'Releasing...' : 'Release'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
